import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box } from '@machobear/design-system'
import {
  AnimatePresence,
  easeInOut,
  motion,
  useMotionValueEvent,
  useScroll
} from 'framer-motion'

import { Listing } from '@/types'
import structure from '@/structure'
import { breakpoints, colors, fontWeights, zIndex } from '@/styles'
import { useCityData } from '@/lib/locations'
import useMediaQuery from '@/hooks/use-media-query'
import useRole from '@/hooks/use-role'
import BlurredText from '@/components/blurred-text'
import { CityBreadcrumbs } from '@/components/breadcrumbs'
import { Button } from '@/components/button-new'
import { HeartSimpleIcon, ShareAlt } from '@/components/icons'
import ListingMessageHeader from '@/components/listing-message-header'
import { H1 } from '@/components/text'
import { Tooltip } from '@/components/tooltip'
import UnverifiedBadge from '@/components/unverified-badge'
import VerifiedBadge from '@/components/verified-badge'

import { IMPORTED_LISTING_PLATFORM } from '../page'

type Props = {
  address: string
  listings: Listing
  isFavourite: boolean
  isManagingLandlord: boolean
  setShowTopNav: (value: boolean) => void
  handleFavourite: () => void
  handleShare: () => void
  isListingLocked: boolean
}

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  padding: 16px 20px;
  border-top: 1px solid ${colors.grey88};
  border-bottom: 1px solid ${colors.grey88};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    height: 90px;
  }
`

const Test = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: ${structure.page.width}px;
  width: 100%;
`

const ControlButtons = styled.div`
  display: flex;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    display: none;
  }
`

const StyledButton = styled(Button)`
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  max-width: 150px;
  height: 48px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    border-radius: 64px;
    width: 54px;
    height: 54px;
    padding: 5px;
  }
`

const ButtonText = styled.div`
  display: flex;
  align-items: center;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`

const AddressRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
`

const Address = styled(H1)`
  color: ${colors.black};
  font-size: 20px;
  line-height: 28px;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 16px;
    line-height: 20px;
  }
`

const FavouriteIcon = styled(HeartSimpleIcon)<{ $isFavourite: boolean }>`
  > path {
    fill: ${props => (props.$isFavourite ? colors.blue16 : 'none')};
  }
`

const FixedHeader = ({
  address,
  listings,
  isFavourite,
  setShowTopNav,
  handleFavourite,
  handleShare,
  isListingLocked
}: Props) => {
  const { t } = useTranslation()
  const router = useRouter()
  const role = useRole()
  const citySlug = router?.query?.city || []
  const { data: cityData, loading: cityLoading } = useCityData(citySlug)
  const hideButtons = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const listingVerified = listings.verified_state_machine === 'approved'
  const listingState =
    listings.is_hidden === '1' ? 'hidden' : listings.state_machine
  const showMessageHeader = listingState !== 'published' && role === 'landlord'
  const isImported = listings?.tags?.some((tag: string) =>
    IMPORTED_LISTING_PLATFORM.includes(tag)
  )
  const addressVerifiedText = t(
    'b.listing.view.address_verified.tooltip',
    'This listing is address verified.'
  )
  const [hideTopNavbar, setHideTopNavbar] = useState(false)
  const [hideMessageBar, setHideMessageBar] = useState(false)

  const { scrollYProgress } = useScroll()

  const variants = {
    visible: { top: hideButtons ? '60px' : '100px' },
    hidden: { top: hideButtons ? '60px' : 0 }
  }

  useMotionValueEvent(scrollYProgress, 'change', latest => {
    const prev = scrollYProgress?.getPrevious()
    if (latest > prev) {
      setHideTopNavbar(true)
      setShowTopNav(false)
    } else {
      setHideTopNavbar(false)
      setShowTopNav(true)
    }

    if (latest > 10) {
      setHideMessageBar(true)
    } else {
      setHideMessageBar(false)
    }
  })

  return (
    <>
      <motion.div
        initial={'hidden'}
        variants={variants}
        animate={hideTopNavbar ? 'hidden' : 'visible'}
        transition={{
          ease: easeInOut,
          type: 'spring',
          damping: 18
        }}
        style={{
          zIndex: zIndex.siteHeader,
          position: 'sticky',
          width: '100%'
        }}
      >
        <Box width="full" background="white" zIndex="notification">
          <HeaderContent>
            <Test>
              <Column>
                <AddressRow>
                  {isListingLocked ? (
                    <BlurredText variant={{ size: 'large' }}>*****</BlurredText>
                  ) : (
                    <Address as="h1">{address}</Address>
                  )}

                  {listingVerified ? (
                    <Tooltip
                      text={addressVerifiedText}
                      position="bottomLeft"
                      left={2}
                      bottom={-1}
                    >
                      <VerifiedBadge
                        verified
                        type="listing"
                        height={24}
                        width={24}
                      />
                    </Tooltip>
                  ) : (
                    <UnverifiedBadge />
                  )}
                </AddressRow>
                <CityBreadcrumbs
                  cityData={cityData}
                  loading={cityLoading}
                  bottom={0}
                />
              </Column>
              {!hideButtons && (
                <ControlButtons>
                  <StyledButton
                    id="listing-details-click-favourite"
                    onClick={handleFavourite}
                    theme="secondary"
                    size="unset"
                  >
                    <FavouriteIcon
                      $isFavourite={isFavourite}
                      height={16}
                      width={16}
                    />
                    <ButtonText>
                      {isFavourite
                        ? t(
                            'listing:b.listing.view.unfavourite.text',
                            'Unfavourite'
                          )
                        : t(
                            'listing:b.listing.view.favourite.text',
                            'Favourite'
                          )}
                    </ButtonText>
                  </StyledButton>
                  <StyledButton
                    id="listing-detail-share-click"
                    onClick={handleShare}
                    theme="secondary"
                    size="unset"
                  >
                    <ShareAlt height={16} width={16} />
                    <ButtonText>
                      {t('listing:b.listing.view.share.button', 'Share')}
                    </ButtonText>
                  </StyledButton>
                </ControlButtons>
              )}
            </Test>
          </HeaderContent>
        </Box>
      </motion.div>
      {showMessageHeader && !hideMessageBar && (
        <AnimatePresence>
          <motion.div
            initial={{ display: 'flex' }}
            animate={
              hideMessageBar
                ? { display: 'none', opacity: 0 }
                : { display: 'flex', opacity: 1 }
            }
            transition={{
              ease: easeInOut,
              type: 'spring',
              damping: 18
            }}
          >
            <ListingMessageHeader
              type={listingState}
              listingId={listings.id}
              externalInfo={listings.external_info}
              isImported={isImported}
            />
          </motion.div>
        </AnimatePresence>
      )}
    </>
  )
}

export default FixedHeader
